import React, { useState, useEffect, useContext } from 'react'
import { motion } from 'framer-motion'
import ReactECharts from 'echarts-for-react'
import { format } from 'date-fns'
import { SupabaseService } from '../../services/supabaseService'
import { AnalyticsService } from '../../services/analyticsService'
import { CreditService } from '../../services/creditService'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import { ThemeContext } from '../../App'

const { FiBarChart2, FiTrendingUp, FiSearch, FiRefreshCw, FiCreditCard, FiClock, FiCheckCircle, FiPlus, FiStar, FiInfo, FiZap, FiLoader, FiBarChart, FiActivity, FiMail, FiChevronLeft, FiChevronRight, FiExternalLink } = FiIcons

const AnalyticsDashboard = ({ user, onSearch, onLoadSearch, userCredits, onPurchaseClick, setActiveTab }) => {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalSearches: 0,
    totalOpportunities: 0,
    categoryBreakdown: {},
    statusBreakdown: { new: 0, submitted: 0, live: 0 },
    recentSearches: [],
    searchTrend: [],
    relatedKeywords: [],
    timeSaved: 0
  })
  const [creditHistory, setCreditHistory] = useState([])
  const [creditHistoryPage, setCreditHistoryPage] = useState(1)
  const [creditHistoryTotalPages, setCreditHistoryTotalPages] = useState(1)
  const [refreshing, setRefreshing] = useState(false)
  const [keywordError, setKeywordError] = useState('')
  const [error, setError] = useState('')
  const [liveBacklinks, setLiveBacklinks] = useState([])
  const [submittedBacklinks, setSubmittedBacklinks] = useState([])
  const [liveBacklinksPage, setLiveBacklinksPage] = useState(1)
  const [submittedBacklinksPage, setSubmittedBacklinksPage] = useState(1)
  const [liveBacklinksTotalPages, setLiveBacklinksTotalPages] = useState(1)
  const [submittedBacklinksTotalPages, setSubmittedBacklinksTotalPages] = useState(1)
  const { theme } = useContext(ThemeContext)

  const loadStats = async () => {
    try {
      setRefreshing(true)
      setError('')
      console.log('Loading analytics for user:', user?.id)
      const analyticsData = await AnalyticsService.getUserAnalytics(user.id)
      console.log('Analytics data received:', analyticsData)

      // Get directory stats separately
      const { data: directoryStats, error: directoryError } = await SupabaseService.supabase
        .from('user_link_directory_actions')
        .select('status')
        .eq('user_id', user.id)
        .in('status', ['Submitted', 'Live'])

      if (directoryError) {
        console.error('Error fetching directory stats:', directoryError)
      }

      // Count directory submitted and live statuses
      const directorySubmitted = directoryStats ? directoryStats.filter(item => item.status === 'Submitted').length : 0
      const directoryLive = directoryStats ? directoryStats.filter(item => item.status === 'Live').length : 0

      // Combine both analytics data and directory stats
      const combinedStatusBreakdown = {
        new: analyticsData.statusBreakdown.new || 0,
        submitted: (analyticsData.statusBreakdown.submitted || 0) + directorySubmitted,
        live: (analyticsData.statusBreakdown.live || 0) + directoryLive
      }

      setStats({ ...analyticsData, statusBreakdown: combinedStatusBreakdown })

      // Load live and submitted backlinks
      await loadLiveBacklinks()
      await loadSubmittedBacklinks()

      // Load credit history
      await loadCreditHistory(1)
      console.log('Credit history loaded')
    } catch (error) {
      console.error('Error loading analytics:', error)
      setError('Failed to load analytics data. Please try refreshing.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const loadLiveBacklinks = async () => {
    try {
      // Get backlinks from searches with 'live' status
      const { data: searchLiveBacklinks, error: searchError } = await SupabaseService.supabase
        .from('backlink_status_rtk7309')
        .select(`
          status,
          result_id,
          search_id,
          searches:search_id (
            keyword,
            results
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'live')

      if (searchError) throw searchError

      // Get backlinks from directory with 'Live' status
      const { data: directoryLiveBacklinks, error: directoryError } = await SupabaseService.supabase
        .from('user_link_directory_actions')
        .select(`
          status,
          link_id,
          directory:link_id (
            link_opportunity,
            type,
            niche
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'Live')

      if (directoryError) throw directoryError

      // Process search backlinks
      const processedSearchBacklinks = searchLiveBacklinks
        .filter(item => item.searches && item.searches.results)
        .map(item => {
          const result = item.searches.results[parseInt(item.result_id)]
          if (!result) return null
          return {
            url: result.url,
            type: result.category || 'Other',
            niche: item.searches.keyword,
            source: 'search',
            search_id: item.search_id
          }
        })
        .filter(Boolean)

      // Process directory backlinks
      const processedDirectoryBacklinks = directoryLiveBacklinks
        .filter(item => item.directory)
        .map(item => ({
          url: item.directory.link_opportunity,
          type: item.directory.type || 'Other',
          niche: item.directory.niche,
          source: 'directory',
          link_id: item.link_id
        }))

      // Combine all backlinks
      const combined = [...processedSearchBacklinks, ...processedDirectoryBacklinks]

      // Calculate pagination
      const itemsPerPage = 15
      setLiveBacklinksTotalPages(Math.ceil(combined.length / itemsPerPage))

      // Set the state with all backlinks (we'll handle pagination in the render)
      setLiveBacklinks(combined)
    } catch (error) {
      console.error('Error loading live backlinks:', error)
    }
  }

  const loadSubmittedBacklinks = async () => {
    try {
      // Get backlinks from searches with 'submitted' status
      const { data: searchSubmittedBacklinks, error: searchError } = await SupabaseService.supabase
        .from('backlink_status_rtk7309')
        .select(`
          status,
          result_id,
          search_id,
          searches:search_id (
            keyword,
            results
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'submitted')

      if (searchError) throw searchError

      // Get backlinks from directory with 'Submitted' status
      const { data: directorySubmittedBacklinks, error: directoryError } = await SupabaseService.supabase
        .from('user_link_directory_actions')
        .select(`
          status,
          link_id,
          directory:link_id (
            link_opportunity,
            type,
            niche
          )
        `)
        .eq('user_id', user.id)
        .eq('status', 'Submitted')

      if (directoryError) throw directoryError

      // Process search backlinks
      const processedSearchBacklinks = searchSubmittedBacklinks
        .filter(item => item.searches && item.searches.results)
        .map(item => {
          const result = item.searches.results[parseInt(item.result_id)]
          if (!result) return null
          return {
            url: result.url,
            type: result.category || 'Other',
            niche: item.searches.keyword,
            source: 'search',
            search_id: item.search_id
          }
        })
        .filter(Boolean)

      // Process directory backlinks
      const processedDirectoryBacklinks = directorySubmittedBacklinks
        .filter(item => item.directory)
        .map(item => ({
          url: item.directory.link_opportunity,
          type: item.directory.type || 'Other',
          niche: item.directory.niche,
          source: 'directory',
          link_id: item.link_id
        }))

      // Combine all backlinks
      const combined = [...processedSearchBacklinks, ...processedDirectoryBacklinks]

      // Calculate pagination
      const itemsPerPage = 15
      setSubmittedBacklinksTotalPages(Math.ceil(combined.length / itemsPerPage))

      // Set the state with all backlinks (we'll handle pagination in the render)
      setSubmittedBacklinks(combined)
    } catch (error) {
      console.error('Error loading submitted backlinks:', error)
    }
  }

  const loadCreditHistory = async (page = 1) => {
    try {
      const [transactions, totalCount] = await Promise.all([
        CreditService.getCreditTransactions(user.id, page, 15),
        CreditService.getCreditTransactionsCount(user.id)
      ])
      setCreditHistory(transactions)
      setCreditHistoryPage(page)
      setCreditHistoryTotalPages(Math.ceil(totalCount / 15))
    } catch (error) {
      console.error('Error loading credit history:', error)
    }
  }

  const handleCreditHistoryPageChange = (page) => {
    if (page >= 1 && page <= creditHistoryTotalPages) {
      loadCreditHistory(page)
    }
  }

  const handleLiveBacklinksPageChange = (page) => {
    if (page >= 1 && page <= liveBacklinksTotalPages) {
      setLiveBacklinksPage(page)
    }
  }

  const handleSubmittedBacklinksPageChange = (page) => {
    if (page >= 1 && page <= submittedBacklinksTotalPages) {
      setSubmittedBacklinksPage(page)
    }
  }

  useEffect(() => {
    if (user?.id) {
      loadStats()
    }
  }, [user])

  const handleKeywordClick = async (keyword) => {
    setKeywordError('')
    // Check if user has credits
    if (userCredits < 1) {
      setKeywordError('You need at least 1 credit to perform a search. Please purchase credits to continue.')
      return
    }
    // Switch to search tab before performing search
    if (setActiveTab) {
      setActiveTab('search')
    }
    // Perform the search
    onSearch(keyword)
  }

  const handleNicheClick = (backlink) => {
    if (backlink.source === 'search') {
      // Load the search results for this keyword
      const searchToLoad = { id: backlink.search_id, keyword: backlink.niche }
      onLoadSearch(searchToLoad)
    } else if (backlink.source === 'directory') {
      // Navigate to the Link Directory view when clicked
      // Now pass both niche and type for filtering
      onLoadSearch({ isDirectory: true, niche: backlink.niche, linkType: backlink.type })
    }
  }

  const getCategoryColors = () => {
    return {
      'Blog Comments': '#3B82F6',
      'Guest Posts': '#10B981',
      'Forum Comments': '#8B5CF6',
      'Resource Pages': '#D59A4C',
      'Other': '#6B7280'
    }
  }

  const truncateUrl = (url, maxLength = 40) => {
    if (!url) return '';
    try {
      const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`);
      const domain = urlObj.hostname;
      const path = urlObj.pathname;
      if (domain.length + 10 >= maxLength) {
        // If domain itself is too long, truncate it
        return domain.substring(0, maxLength - 10) + '...' + domain.substring(domain.length - 7);
      }
      // Keep domain and truncate the middle of the path
      const pathParts = path.split('/').filter(Boolean);
      if (pathParts.length <= 2) {
        // If path is short, just show it all
        return `${domain}${path}`;
      }
      const firstPart = pathParts[0];
      const lastPart = pathParts[pathParts.length - 1];
      return `${domain}/${firstPart}/.../${lastPart}`;
    } catch (e) {
      // If URL parsing fails, do simple truncation
      if (url.length <= maxLength) return url;
      return url.substring(0, maxLength/2) + '...' + url.substring(url.length - maxLength/2);
    }
  };

  const getBacklinkTypeColor = (type) => {
    const colors = {
      'Blog Comments': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'Guest Posts': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'Forum Comments': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      'Resource Pages': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      'Guest Post': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      'Blog Comment': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      'Resource Page': 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      'Forum Comment': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      'Other': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    }
    return colors[type] || colors['Other']
  }

  // Create category chart option
  const categoryChartOption = {
    backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff', // Dynamic background color based on theme
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: '{b}: {c} opportunities',
      backgroundColor: theme === 'dark' ? '#374151' : '#ffffff',
      borderColor: theme === 'dark' ? '#4B5563' : '#E5E7EB',
      textStyle: {
        color: theme === 'dark' ? '#F9FAFB' : '#111827'
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      containLabel: true
    },
    xAxis: {
      type: 'value',
      axisLabel: {
        fontWeight: 'bold',
        fontSize: 14, // Increased font size by 4px (default is ~10px)
        color: theme === 'dark' ? '#9CA3AF' : '#6B7280'
      },
      splitLine: {
        lineStyle: {
          color: theme === 'dark' ? '#374151' : '#E5E7EB'
        }
      }
    },
    yAxis: {
      type: 'category',
      data: Object.keys(stats.categoryBreakdown || {}),
      axisLabel: {
        interval: 0,
        fontWeight: 'bold',
        fontSize: 14, // Increased font size by 4px (default is ~10px)
        color: theme === 'dark' ? '#9CA3AF' : '#6B7280'
      },
      splitLine: {
        lineStyle: {
          color: theme === 'dark' ? '#374151' : '#E5E7EB'
        }
      }
    },
    series: [
      {
        name: 'Opportunities',
        type: 'bar',
        data: Object.entries(stats.categoryBreakdown || {}).map(([name, value]) => ({
          value,
          itemStyle: {
            color: getCategoryColors()[name] || '#6B7280',
            borderRadius: [0, 5, 5, 0],
            shadowColor: 'rgba(0,0,0,0.2)',
            shadowBlur: 10,
            shadowOffsetX: 2,
            shadowOffsetY: 2
          }
        }))
      }
    ]
  }

  // Create trend chart option
  const trendChartOption = {
    backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff', // Dynamic background color based on theme
    tooltip: {
      trigger: 'axis',
      backgroundColor: theme === 'dark' ? '#374151' : '#ffffff',
      borderColor: theme === 'dark' ? '#4B5563' : '#E5E7EB',
      textStyle: {
        color: theme === 'dark' ? '#F9FAFB' : '#111827'
      }
    },
    xAxis: {
      type: 'category',
      data: (stats.searchTrend || []).map(item => format(new Date(item.date), 'MMM d')),
      axisLabel: {
        color: theme === 'dark' ? '#9CA3AF' : '#6B7280'
      },
      splitLine: {
        lineStyle: {
          color: theme === 'dark' ? '#374151' : '#E5E7EB'
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        color: theme === 'dark' ? '#9CA3AF' : '#6B7280'
      },
      splitLine: {
        lineStyle: {
          color: theme === 'dark' ? '#374151' : '#E5E7EB'
        }
      }
    },
    series: [
      {
        name: 'Searches',
        type: 'line',
        smooth: true,
        data: (stats.searchTrend || []).map(item => item.count),
        areaStyle: {
          opacity: 0.3
        },
        itemStyle: {
          color: '#D59A4C'
        },
        lineStyle: {
          color: '#D59A4C'
        },
        areaStyle: {
          color: '#D59A4C',
          opacity: 0.2
        }
      }
    ]
  }

  if (loading && !refreshing) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 dark:bg-gray-800">
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D59A4C]"></div>
        </div>
      </div>
    )
  }

  // Helper function to format transaction display
  const formatTransactionDisplay = (transaction) => {
    if (transaction.transaction_type === 'purchase') {
      return `Purchased ${transaction.amount} credits`
    }
    if (transaction.transaction_type === 'usage') {
      if (transaction.description === 'content_generation') {
        // Always display this as 0.1 regardless of what's in the amount field
        return 'Used 0.1 credits for content generation'
      }
      if (transaction.description === 'email_extraction') {
        return 'Used 0.1 credits for email extraction'
      }
      if (Math.abs(transaction.amount) === 1) {
        return 'Used 1 credit for search'
      }
      return `Used ${Math.abs(transaction.amount)} credits`
    }
    if (transaction.transaction_type === 'bonus') {
      return transaction.description || 'Bonus credits'
    }
    return 'Credit transaction'
  }

  // Show all related keywords instead of splitting into two rows
  const allRelatedKeywords = stats.relatedKeywords || [];

  // Get paginated backlinks
  const itemsPerPage = 15;
  const startIndexLive = (liveBacklinksPage - 1) * itemsPerPage;
  const endIndexLive = startIndexLive + itemsPerPage;
  const paginatedLiveBacklinks = liveBacklinks.slice(startIndexLive, endIndexLive);

  const startIndexSubmitted = (submittedBacklinksPage - 1) * itemsPerPage;
  const endIndexSubmitted = startIndexSubmitted + itemsPerPage;
  const paginatedSubmittedBacklinks = submittedBacklinks.slice(startIndexSubmitted, endIndexSubmitted);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      {/* Header with refresh button */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2 dark:text-white">Analytics Dashboard</h2>
          <p className="text-gray-600 dark:text-gray-400">Insights into your backlink opportunities</p>
        </div>
        <button
          onClick={loadStats}
          disabled={refreshing}
          className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 dark:bg-red-900/30 dark:border-red-600">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Summary Cards - Now 5 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 dark:bg-gray-800">
          <div className="flex items-start">
            <div className="bg-[#D59A4C20] p-3 rounded-lg mr-3">
              <SafeIcon icon={FiSearch} className="w-6 h-6 text-[#D59A4C]" />
            </div>
            <div>
              <p className="text-gray-600 text-sm dark:text-gray-400">Total Searches</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSearches || 0}</h3>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 dark:bg-gray-800">
          <div className="flex items-start">
            <div className="bg-[#3B82F620] p-3 rounded-lg mr-3">
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-[#3B82F6]" />
            </div>
            <div>
              <p className="text-gray-600 text-sm whitespace-nowrap dark:text-gray-400">Total Opportunities</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalOpportunities || 0}</h3>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 dark:bg-gray-800">
          <div className="flex items-start">
            <div className="bg-[#F59E0B20] p-3 rounded-lg mr-3">
              <SafeIcon icon={FiLoader} className="w-6 h-6 text-[#F59E0B]" />
            </div>
            <div>
              <p className="text-gray-600 text-sm whitespace-nowrap dark:text-gray-400">Submitted Backlinks</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{stats.statusBreakdown?.submitted || 0}</h3>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 dark:bg-gray-800">
          <div className="flex items-start">
            <div className="bg-[#10B98120] p-3 rounded-lg mr-3">
              <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-[#10B981]" />
            </div>
            <div>
              <p className="text-gray-600 text-sm dark:text-gray-400">Live Backlinks</p>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{stats.statusBreakdown?.live || 0}</h3>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 dark:bg-gray-800">
          <div className="flex items-start">
            <div className="bg-[#8B5CF620] p-3 rounded-lg mr-3">
              <SafeIcon icon={FiClock} className="w-6 h-6 text-[#8B5CF6]" />
            </div>
            <div>
              <p className="text-gray-600 text-sm dark:text-gray-400">Time Saved</p>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {stats.timeSaved || 0} hours
              </h3>
            </div>
          </div>
        </div>
      </div>

      {/* Opportunity Types Chart - Full Width */}
      <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
        <div className="flex items-center mb-2">
          <div className="bg-[#D59A4C20] p-2 rounded-lg mr-3">
            <SafeIcon icon={FiStar} className="w-5 h-5 text-[#D59A4C]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Opportunity Types</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">A breakdown of link opportunities found in your searches</p>
        {Object.keys(stats.categoryBreakdown || {}).length > 0 ? (
          <ReactECharts
            option={categoryChartOption}
            style={{ height: '300px' }}
            className="w-full"
            notMerge={true}
            theme={theme === 'dark' ? 'dark' : ''}
          />
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-500 dark:text-gray-400">
            No opportunity data available yet. Start searching to see your breakdown!
          </div>
        )}
      </div>

      {/* Related Keywords Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
        <div className="flex items-center mb-2">
          <div className="bg-[#D59A4C20] p-2 rounded-lg mr-3">
            <SafeIcon icon={FiInfo} className="w-5 h-5 text-[#D59A4C]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Related Keyword Ideas</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">Click a keyword idea to start a new search for more backlink opportunities</p>

        {keywordError && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4 dark:bg-red-900/30 dark:border-red-600">
            <p className="text-red-700 text-sm dark:text-red-400">
              You need at least 1 credit to perform a search. Please{' '}
              <button
                onClick={onPurchaseClick}
                className="text-blue-600 hover:text-blue-800 underline no-border dark:text-blue-400 dark:hover:text-blue-300"
              >
                purchase credits
              </button>{' '}
              to continue.
            </p>
          </div>
        )}

        {!allRelatedKeywords || allRelatedKeywords.length === 0 ? (
          <p className="text-gray-600 text-center py-6 dark:text-gray-400">No related keywords found yet. Try more searches!</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {allRelatedKeywords.map((keyword, index) => (
              <button
                key={index}
                onClick={() => handleKeywordClick(keyword)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-full text-sm transition-colors dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              >
                {keyword}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Recent Searches */}
      <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
        <div className="flex items-center mb-2">
          <div className="bg-[#D59A4C20] p-2 rounded-lg mr-3">
            <SafeIcon icon={FiSearch} className="w-5 h-5 text-[#D59A4C]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Searches</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">Click any keyword to view backlink opportunities</p>

        {!stats.recentSearches || stats.recentSearches.length === 0 ? (
          <p className="text-gray-600 text-center py-6 dark:text-gray-400">No searches yet</p>
        ) : (
          <div className="space-y-2">
            {stats.recentSearches.map((search, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors dark:bg-gray-700 dark:hover:bg-gray-600"
                onClick={() => onLoadSearch(search)}
              >
                <div className="flex items-center">
                  <SafeIcon icon={FiSearch} className="w-4 h-4 text-gray-400 mr-3 dark:text-gray-500" />
                  <span className="font-medium text-gray-800 dark:text-gray-300">{search.keyword}</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {format(new Date(search.created_at), 'MMM d, yyyy')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Live Backlinks Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
        <div className="flex items-center mb-2">
          <div className="bg-[#10B98120] p-2 rounded-lg mr-3">
            <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-[#10B981]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Live Backlinks</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">Your backlinks that are currently live</p>

        {liveBacklinks.length === 0 ? (
          <p className="text-gray-600 text-center py-6 dark:text-gray-400">No live backlinks yet</p>
        ) : (
          <div>
            {/* Header Row */}
            <div className="grid grid-cols-3 gap-3 mb-2 px-3 py-2 font-medium text-gray-700 dark:text-gray-300 border-b dark:border-gray-700">
              <div>Niche</div>
              <div>Link Type</div>
              <div>Link URL</div>
            </div>

            {/* Backlinks */}
            <div className="space-y-2 mb-4">
              {paginatedLiveBacklinks.map((backlink, index) => (
                <div
                  key={index}
                  className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg transition-all duration-200 hover:bg-gray-100 hover:shadow-md dark:bg-gray-700 dark:hover:bg-gray-600"
                >
                  {/* Column 1: Niche/Keyword (clickable) */}
                  <div>
                    <button
                      onClick={() => handleNicheClick(backlink)}
                      className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 px-1 py-1 md:border-2 md:border-[#2D3748] md:rounded-[40px]"
                    >
                      {backlink.niche}
                    </button>
                  </div>

                  {/* Column 2: Link Type (with color tag) */}
                  <div>
                    <span className={`px-3 py-1 rounded-full text-xs ${getBacklinkTypeColor(backlink.type)}`}>
                      {backlink.type}
                    </span>
                  </div>

                  {/* Column 3: Backlink URL (truncated, clickable) - Show only icon on mobile */}
                  <div className="flex items-center flex-1 min-w-0">
                    <a
                      href={backlink.url?.startsWith('http') ? backlink.url : `https://${backlink.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-600 hover:text-blue-800 truncate dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
                    >
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0 dark:text-gray-500" />
                      <span className="hidden md:inline">{truncateUrl(backlink.url)}</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {liveBacklinksTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Page {liveBacklinksPage} of {liveBacklinksTotalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleLiveBacklinksPageChange(liveBacklinksPage - 1)}
                    disabled={liveBacklinksPage === 1}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" />
                    Previous
                  </button>
                  <button
                    onClick={() => handleLiveBacklinksPageChange(liveBacklinksPage + 1)}
                    disabled={liveBacklinksPage === liveBacklinksTotalPages}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Next
                    <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Submitted Backlinks Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
        <div className="flex items-center mb-2">
          <div className="bg-[#F59E0B20] p-2 rounded-lg mr-3">
            <SafeIcon icon={FiLoader} className="w-5 h-5 text-[#F59E0B]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Submitted Backlinks</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4 dark:text-gray-400">Your backlinks that have been submitted but not yet live</p>

        {submittedBacklinks.length === 0 ? (
          <p className="text-gray-600 text-center py-6 dark:text-gray-400">No submitted backlinks yet</p>
        ) : (
          <div>
            {/* Header Row */}
            <div className="grid grid-cols-3 gap-3 mb-2 px-3 py-2 font-medium text-gray-700 dark:text-gray-300 border-b dark:border-gray-700">
              <div>Niche</div>
              <div>Link Type</div>
              <div>Link URL</div>
            </div>

            {/* Backlinks */}
            <div className="space-y-2 mb-4">
              {paginatedSubmittedBacklinks.map((backlink, index) => (
                <div
                  key={index}
                  className="grid grid-cols-3 gap-3 p-3 bg-gray-50 rounded-lg transition-all duration-200 hover:bg-gray-100 hover:shadow-md dark:bg-gray-700 dark:hover:bg-gray-600"
                >
                  {/* Column 1: Niche/Keyword (clickable) */}
                  <div>
                    <button
                      onClick={() => handleNicheClick(backlink)}
                      className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 px-1 py-1 md:border-2 md:border-[#2D3748] md:rounded-[40px]"
                    >
                      {backlink.niche}
                    </button>
                  </div>

                  {/* Column 2: Link Type (with color tag) */}
                  <div>
                    <span className={`px-3 py-1 rounded-full text-xs ${getBacklinkTypeColor(backlink.type)}`}>
                      {backlink.type}
                    </span>
                  </div>

                  {/* Column 3: Backlink URL (truncated, clickable) - Show only icon on mobile */}
                  <div className="flex items-center flex-1 min-w-0">
                    <a
                      href={backlink.url?.startsWith('http') ? backlink.url : `https://${backlink.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-600 hover:text-blue-800 truncate dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
                    >
                      <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0 dark:text-gray-500" />
                      <span className="hidden md:inline">{truncateUrl(backlink.url)}</span>
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {submittedBacklinksTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Page {submittedBacklinksPage} of {submittedBacklinksTotalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleSubmittedBacklinksPageChange(submittedBacklinksPage - 1)}
                    disabled={submittedBacklinksPage === 1}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" />
                    Previous
                  </button>
                  <button
                    onClick={() => handleSubmittedBacklinksPageChange(submittedBacklinksPage + 1)}
                    disabled={submittedBacklinksPage === submittedBacklinksTotalPages}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Next
                    <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search Activity Chart */}
      <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
        <div className="flex items-center mb-2">
          <div className="bg-[#D59A4C20] p-2 rounded-lg mr-3">
            <SafeIcon icon={FiActivity} className="w-5 h-5 text-[#D59A4C]" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Search Activity</h3>
        </div>

        {stats.searchTrend && stats.searchTrend.length > 0 ? (
          <ReactECharts
            option={trendChartOption}
            style={{ height: '250px' }}
            className="w-full"
            notMerge={true}
            theme={theme === 'dark' ? 'dark' : ''}
          />
        ) : (
          <div className="h-48 flex items-center justify-center text-gray-500 dark:text-gray-400">
            No search activity data available yet.
          </div>
        )}
      </div>

      {/* Credits Section - Moved to bottom */}
      <div className="bg-white rounded-xl shadow-lg p-6 dark:bg-gray-800">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Credits Remaining & Usage History</h3>
          <button
            onClick={onPurchaseClick}
            className="flex items-center px-4 py-2 bg-[#D59A4C] text-white rounded-lg hover:bg-[#B58C54] transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
            Buy Credits
          </button>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg dark:bg-gray-700">
            <div className="flex items-center">
              <div className="bg-[#D59A4C30] p-2 rounded-lg mr-3">
                <SafeIcon icon={FiCreditCard} className="w-5 h-5 text-[#D59A4C]" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Credits Remaining</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{userCredits.toFixed(1) || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {creditHistory.length === 0 ? (
          <p className="text-gray-600 text-center py-6 dark:text-gray-400">No credit transactions yet</p>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            <h4 className="font-medium text-gray-900 mb-3 dark:text-white">Recent Usage</h4>
            {creditHistory.map((transaction) => (
              <div
                key={transaction.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg dark:bg-gray-700"
              >
                <div className="flex items-center">
                  <div className={`p-2 rounded-lg mr-3 ${transaction.transaction_type === 'purchase' ? 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400' : 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400'}`}>
                    <SafeIcon
                      icon={transaction.transaction_type === 'purchase' ? FiTrendingUp : transaction.description === 'content_generation' ? FiZap : transaction.description === 'email_extraction' ? FiMail : FiSearch}
                      className="w-4 h-4"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatTransactionDisplay(transaction)}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {format(new Date(transaction.created_at), 'MMM d, yyyy HH:mm')}
                    </p>
                  </div>
                </div>
                <div className={`text-lg font-semibold ${transaction.amount > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {transaction.amount > 0 ? '+' : ''}
                  {transaction.description === 'content_generation' || transaction.description === 'email_extraction' ? '-0.1' : transaction.amount.toFixed(1)}
                </div>
              </div>
            ))}

            {/* Pagination for credit history */}
            {creditHistoryTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t dark:border-gray-700">
                <div className="text-sm text-gray-700 dark:text-gray-300">
                  Page {creditHistoryPage} of {creditHistoryTotalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleCreditHistoryPageChange(creditHistoryPage - 1)}
                    disabled={creditHistoryPage === 1}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <SafeIcon icon={FiChevronLeft} className="w-4 h-4 mr-1" />
                    Previous
                  </button>
                  <button
                    onClick={() => handleCreditHistoryPageChange(creditHistoryPage + 1)}
                    disabled={creditHistoryPage === creditHistoryTotalPages}
                    className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    Next
                    <SafeIcon icon={FiChevronRight} className="w-4 h-4 ml-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default AnalyticsDashboard